import React, { useEffect, useRef, useState } from 'react';
import { Plane, Download, Loader2, CheckCircle2, Clock, Flame, Activity } from 'lucide-react';
import { XCircle, ChartBar } from '@phosphor-icons/react';

import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { projectsService } from '../../services/projects';
import UnifiedAirportMap from '../../components/map-engine/UnifiedAirportMap';
import AviPLANPanel, { type AviPLANData } from '../../components/map-engine/AviPLANPanel';
import MetricCard from '../../components/projects/MetricCard';
import { useProject } from './ProjectContext';
import { useProjectAnalysis } from './useProjectAnalysis';

/**
 * Airport Map tab — Mapbox topology overlay + FlightAware track download panel
 * + top-10 busiest days table. Lifted verbatim from ProjectDetailPage.tsx
 * L2377-2736 (JSX), L802-811 (state), L845-891 (handlers), L1210-1226 (effect).
 *
 * Two behavior fixes made during the move (see MEMORY / task brief):
 *  1. The track-download poll interval now lives in a ref and is cleared on
 *     unmount — in the monolith the tab never unmounted mid-download, so the
 *     leak was latent; on its own route the user can navigate away while a
 *     download is running.
 *  2. `ftTaskId` was written and cleared but never read anywhere in render,
 *     so it's dropped entirely (the task id only needs to live in the closure
 *     of the poll callback, not in state).
 */
const MapPage: React.FC = () => {
  const { project, reload, airportCenter } = useProject();
  const { apcas, safeNum } = useProjectAnalysis();
  const { theme } = useTheme();
  const { language } = useLanguage();

  // Surfaces are theme-aware via CSS custom properties (--aipron-*); isDark is only
  // needed because MetricCard still takes it as a prop.
  const isDark = theme === 'dark';

  // AviPLAN geometry overlay (merged in from the former separate heatmap view)
  const [aviplanData, setAviplanData] = useState<AviPLANData>({});
  const textMain = 'text-[var(--text-primary)]';
  const textSub = 'text-[var(--text-secondary)]';

  // FlightAware Track Download
  const [ftStartDate, setFtStartDate] = useState('');
  const [ftEndDate, setFtEndDate] = useState('');
  const [ftUseTop10, setFtUseTop10] = useState(true); // default: use top-N busiest days
  const [ftTopN, setFtTopN] = useState(3); // default to 3 days to control cost (~$94 at $0.06/result)
  const [ftDownloading, setFtDownloading] = useState(false);
  const [ftProgress, setFtProgress] = useState(0);
  const [ftProgressMsg, setFtProgressMsg] = useState('');
  const [ftEstimate, setFtEstimate] = useState<any>(null);
  const [ftError, setFtError] = useState<string | null>(null);

  // Bug fix 1: poll interval lives in a ref so it can be cleared on unmount.
  const ftPollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    return () => {
      if (ftPollIntervalRef.current) clearInterval(ftPollIntervalRef.current);
    };
  }, []);

  // FlightAware download handler
  const handleDownloadTracks = async () => {
    if (!project.id || !ftStartDate || !ftEndDate) return;
    setFtDownloading(true);
    setFtError(null);
    setFtProgress(0);
    setFtProgressMsg('Starting download...');
    try {
      const top10 = project?.analysis_results?.advanced_analysis?.top10_busiest_days;
      const specificDates = ftUseTop10 && top10?.days?.length
        ? (top10.days as any[]).slice(0, ftTopN).map((d: any) => d.date)
        : undefined;
      const result = await projectsService.downloadFlightTracks(project.id, ftStartDate, ftEndDate, specificDates);
      // Poll progress
      ftPollIntervalRef.current = setInterval(async () => {
        try {
          const status = await projectsService.getTaskStatus(result.task_id);
          if (status.progress) setFtProgress(status.progress);
          if (status.message) setFtProgressMsg(status.message);
          if (status.status === 'SUCCESS') {
            if (ftPollIntervalRef.current) clearInterval(ftPollIntervalRef.current);
            ftPollIntervalRef.current = null;
            setFtDownloading(false);
            setFtProgress(100);
            setFtProgressMsg('Download complete!');
            reload(true); // Reload to get new tracks
          } else if (status.status === 'FAILURE') {
            if (ftPollIntervalRef.current) clearInterval(ftPollIntervalRef.current);
            ftPollIntervalRef.current = null;
            setFtDownloading(false);
            setFtError(status.error || 'Download failed');
          }
        } catch (e) { console.warn('Track poll error', e); }
      }, 3000);
    } catch (error: any) {
      setFtError(error?.response?.data?.detail || 'Failed to start download');
      setFtDownloading(false);
    }
  };

  // Estimate cost when dates change
  const handleEstimateCost = async () => {
    if (!project.id || !ftStartDate || !ftEndDate) return;
    try {
      const est = await projectsService.estimateFlightTracksCost(project.id, ftStartDate, ftEndDate);
      setFtEstimate(est);
    } catch (e) { console.warn('Estimate error', e); }
  };

  // Auto-populate flight track dates from top-10 busiest days when project loads
  useEffect(() => {
    const top10 = project.analysis_results?.advanced_analysis?.top10_busiest_days;
    if (top10?.date_range_start && top10?.date_range_end) {
      // Top10 data available -> activate toggle and pre-fill dates
      setFtUseTop10(true);
      setFtStartDate(top10.date_range_start);
      setFtEndDate(top10.date_range_end);
      // Cap ftTopN to the actual number of days available
      if (top10?.days?.length) {
        setFtTopN(n => Math.min(n, top10.days.length));
      }
    } else {
      // No top10 data -> fall back to manual mode
      setFtUseTop10(false);
    }
  }, [project.id, project.analysis_results]);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Airport Map Section */}
      <div className="pro-card p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className={`font-bold text-lg ${textMain}`}>
            {language === 'es' ? 'Mapa del Aeropuerto' : 'Airport Map'}
          </h3>
          <span className="badge badge-info">
            {project?.iata_code || 'N/A'} / {project?.icao_code || 'N/A'}
          </span>
        </div>

        {/* FlightAware Track Download Panel */}
        {(() => {
          const top10 = project?.analysis_results?.advanced_analysis?.top10_busiest_days;
          const hasTop10 = !!(top10?.date_range_start && top10?.date_range_end);
          return (
          <div
            className="mb-4 p-4 rounded-lg border"
            style={{ background: 'color-mix(in srgb, var(--aipron-info) 6%, transparent)', borderColor: 'color-mix(in srgb, var(--aipron-info) 25%, transparent)' }}
          >
            {/* Header row */}
            <div className="flex items-center gap-2 mb-3">
              <Plane size={16} className="text-[var(--aipron-info)]" />
              <span className={`text-sm font-bold ${textMain}`}>
                {language === 'es' ? 'Descargar Flight Tracks (FlightAware)' : 'Download Flight Tracks (FlightAware)'}
              </span>
              <span
                className="px-2 py-0.5 rounded text-xs font-mono"
                style={{ background: 'color-mix(in srgb, var(--aipron-info) 12%, transparent)', color: 'var(--aipron-info)' }}
              >AeroAPI</span>

              {/* Top-N toggle + selector — only shown when data is available */}
              {hasTop10 && (
                <div className="ml-auto flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const next = !ftUseTop10;
                      setFtUseTop10(next);
                      setFtEstimate(null);
                      if (next) {
                        setFtStartDate(top10.date_range_start);
                        setFtEndDate(top10.date_range_end);
                      } else {
                        setFtStartDate('');
                        setFtEndDate('');
                      }
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-bold border transition-colors ${
                      ftUseTop10 ? '' : 'bg-[var(--surface-raised)] border-[var(--border)] text-[var(--text-secondary)]'
                    }`}
                    style={ftUseTop10 ? { background: 'color-mix(in srgb, var(--aipron-warning) 15%, transparent)', borderColor: 'color-mix(in srgb, var(--aipron-warning) 40%, transparent)', color: 'var(--aipron-warning)' } : undefined}
                  >
                    <span>{ftUseTop10 ? '★' : '☆'}</span>
                    {language === 'es' ? `Top días de tráfico` : `Top busiest days`}
                  </button>
                  {/* N selector */}
                  {ftUseTop10 && (
                    <div
                      className="flex items-center gap-1 rounded-lg border px-2 py-0.5"
                      style={{ borderColor: 'color-mix(in srgb, var(--aipron-warning) 40%, transparent)', background: 'color-mix(in srgb, var(--aipron-warning) 10%, transparent)' }}
                    >
                      <button
                        type="button"
                        onClick={() => { setFtTopN(n => Math.max(1, n - 1)); setFtEstimate(null); }}
                        className="text-sm font-bold leading-none px-1 text-[var(--aipron-warning)] hover:opacity-70 transition-opacity"
                      >−</button>
                      <span className="text-xs font-bold w-4 text-center text-[var(--aipron-warning)]">{ftTopN}</span>
                      <button
                        type="button"
                        onClick={() => { setFtTopN(n => Math.min(top10?.days?.length || 10, n + 1)); setFtEstimate(null); }}
                        className="text-sm font-bold leading-none px-1 text-[var(--aipron-warning)] hover:opacity-70 transition-opacity"
                      >+</button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Top-N active: show summary + date range */}
            {ftUseTop10 && hasTop10 ? (
              <div
                className="mb-3 px-3 py-2 rounded-lg text-xs flex flex-wrap items-center gap-x-4 gap-y-1 border"
                style={{ background: 'color-mix(in srgb, var(--aipron-warning) 8%, transparent)', borderColor: 'color-mix(in srgb, var(--aipron-warning) 25%, transparent)', color: 'var(--aipron-warning)' }}
              >
                <span className="font-bold">{language === 'es' ? 'Rango:' : 'Range:'}</span>
                <span className="font-mono">{top10.date_range_start} → {top10.date_range_end}</span>
                {(() => {
                  const selectedDays = (top10.days as any[] || []).slice(0, ftTopN);
                  const nFlights = selectedDays.reduce((s: number, d: any) => s + (d.total || d.flights || 0), 0);
                  // Historical rate: $0.06/result set. Each flight = 1 track call + amortised listing overhead
                  // $0.020/page (listing, ~15 flights/page) + $0.060/track
                  const listingPages = Math.ceil(nFlights / 15) * 2; // arrivals + departures
                  const estCost = (listingPages * 0.020 + nFlights * 0.060).toFixed(2);
                  return (
                    <span className="text-[var(--aipron-warning)]">
                      {selectedDays.length} {language === 'es' ? 'días' : 'days'} · ~{nFlights} {language === 'es' ? 'vuelos' : 'flights'} · ~${estCost}
                      <span className={`ml-2 opacity-60 text-[10px]`}>(@$0.06 histórico)</span>
                    </span>
                  );
                })()}
              </div>
            ) : (
              /* Custom date inputs */
              <div className="flex flex-wrap items-end gap-3 mb-3">
                <div>
                  <label className={`text-xs font-bold block mb-1 ${textSub}`}>
                    {language === 'es' ? 'Fecha inicio' : 'Start date'}
                  </label>
                  <input
                    type="date"
                    value={ftStartDate}
                    onChange={(e) => { setFtStartDate(e.target.value); setFtEstimate(null); }}
                    className="input-field w-auto py-1.5"
                  />
                </div>
                <div>
                  <label className={`text-xs font-bold block mb-1 ${textSub}`}>
                    {language === 'es' ? 'Fecha fin' : 'End date'}
                  </label>
                  <input
                    type="date"
                    value={ftEndDate}
                    onChange={(e) => { setFtEndDate(e.target.value); setFtEstimate(null); }}
                    className="input-field w-auto py-1.5"
                  />
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleEstimateCost}
                disabled={!ftStartDate || !ftEndDate}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${!ftStartDate || !ftEndDate
                  ? 'opacity-50 cursor-not-allowed bg-[var(--surface-raised)] text-[var(--text-tertiary)] border-[var(--border)]'
                  : ''
                  }`}
                style={!ftStartDate || !ftEndDate ? undefined : { background: 'color-mix(in srgb, var(--aipron-info) 12%, transparent)', color: 'var(--aipron-info)', borderColor: 'color-mix(in srgb, var(--aipron-info) 30%, transparent)' }}
              >
                {language === 'es' ? 'Estimar Coste' : 'Estimate Cost'}
              </button>
              <button
                onClick={handleDownloadTracks}
                disabled={ftDownloading || !ftStartDate || !ftEndDate}
                className="btn-primary px-4 py-1.5 text-xs"
              >
                {ftDownloading ? (
                  <><Loader2 size={14} className="animate-spin" /> {language === 'es' ? 'Descargando...' : 'Downloading...'}</>
                ) : (
                  <><Download size={14} /> {language === 'es' ? 'Descargar Tracks' : 'Download Tracks'}</>
                )}
              </button>
            </div>
          </div>
          );
        })()}

          {/* Cost Estimate */}
          {ftEstimate && (
            <div className="mt-3 p-3 rounded-lg text-xs bg-[var(--surface-raised)]">
              <div className="flex flex-wrap gap-4">
                <span><strong>{language === 'es' ? 'Vuelos est.' : 'Est. flights'}:</strong> ~{ftEstimate.estimated_flights?.toLocaleString()}</span>
                <span><strong>{language === 'es' ? 'Llamadas API' : 'API calls'}:</strong> ~{ftEstimate.estimated_api_calls?.toLocaleString()}</span>
                <span className="font-bold text-[var(--aipron-info)]"><strong>{language === 'es' ? 'Coste est.' : 'Est. cost'}:</strong> ~${ftEstimate.estimated_cost_usd}</span>
                <span><strong>{language === 'es' ? 'Días' : 'Days'}:</strong> {ftEstimate.days}</span>
              </div>
            </div>
          )}

          {/* Download Progress */}
          {ftDownloading && (
            <div className="mt-3">
              <div className="flex justify-between text-xs mb-1">
                <span className={textSub}>{ftProgressMsg}</span>
                <span className="font-bold text-[var(--aipron-info)]">{ftProgress}%</span>
              </div>
              <div className="w-full h-2 rounded-full bg-[var(--surface-inset)]">
                <div className="h-2 rounded-full transition-all" style={{ width: `${ftProgress}%`, background: 'var(--aipron-accent)' }} />
              </div>
            </div>
          )}

          {/* Error */}
          {ftError && (
            <div
              className="mt-3 p-2 rounded-lg border text-xs flex items-center gap-1.5"
              style={{ background: 'color-mix(in srgb, var(--aipron-danger) 10%, transparent)', borderColor: 'color-mix(in srgb, var(--aipron-danger) 35%, transparent)', color: 'var(--aipron-danger)' }}
            >
              <XCircle size={14} weight="bold" /> {ftError}
            </div>
          )}

          {/* Top 10 Busiest Days (from analysis) */}
          {(() => {
            const top10 = project?.analysis_results?.advanced_analysis?.top10_busiest_days;
            if (!top10 || !top10.days?.length) return null;
            return (
              <div
                className="mt-3 p-3 rounded-lg border"
                style={{ background: 'color-mix(in srgb, var(--aipron-warning) 6%, transparent)', borderColor: 'color-mix(in srgb, var(--aipron-warning) 25%, transparent)' }}
              >
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <ChartBar size={14} weight="duotone" className="text-[var(--aipron-warning)]" />
                    <span className="text-xs font-bold uppercase text-[var(--aipron-warning)]">
                      {language === 'es' ? 'Top 10 Días de Mayor Tráfico' : 'Top 10 Busiest Days'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    {/* Quick select dropdown */}
                    <select
                      className="px-2 py-1 rounded text-[10px] font-mono border bg-[var(--surface-raised)] text-[var(--text-primary)]"
                      style={{ borderColor: 'color-mix(in srgb, var(--aipron-warning) 30%, transparent)' }}
                      value=""
                      onChange={(e) => {
                        if (e.target.value) {
                          setFtStartDate(e.target.value);
                          setFtEndDate(e.target.value);
                          setFtEstimate(null);
                        }
                      }}
                    >
                      <option value="">{language === 'es' ? '↓ Seleccionar día' : '↓ Select day'}</option>
                      {top10.days.map((d: any, i: number) => (
                        <option key={d.date} value={d.date}>
                          #{i + 1} — {d.date} ({d.total} mov: {d.arrivals}A + {d.departures}D)
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => {
                        setFtStartDate(top10.date_range_start);
                        setFtEndDate(top10.date_range_end);
                        setFtEstimate(null);
                      }}
                      className="px-2 py-1 rounded text-[10px] font-bold transition-colors hover:opacity-80"
                      style={{ background: 'color-mix(in srgb, var(--aipron-warning) 20%, transparent)', color: 'var(--aipron-warning)' }}
                    >
                      {language === 'es' ? '⬆ Usar rango completo' : '⬆ Use full range'}
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs text-[var(--text-secondary)]">
                    <thead>
                      <tr className="border-b border-[var(--border)]">
                        <th className="text-left py-1 pr-2 font-bold">#</th>
                        <th className="text-left py-1 pr-2 font-bold">{language === 'es' ? 'Fecha' : 'Date'}</th>
                        <th className="text-right py-1 pr-2 font-bold">{language === 'es' ? 'Lleg.' : 'Arr'}</th>
                        <th className="text-right py-1 pr-2 font-bold">{language === 'es' ? 'Sal.' : 'Dep'}</th>
                        <th className="text-right py-1 font-bold">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {top10.days.map((d: any, i: number) => {
                        const isSelected = ftStartDate === d.date && ftEndDate === d.date;
                        return (
                          <tr
                            key={d.date}
                            onClick={() => {
                              setFtStartDate(d.date);
                              setFtEndDate(d.date);
                              setFtEstimate(null);
                            }}
                            className={`border-b cursor-pointer transition-colors border-[var(--border-subtle)] ${
                              isSelected
                                ? 'text-[var(--aipron-warning)]'
                                : i === 0
                                  ? 'text-[var(--aipron-warning)] hover:bg-[var(--surface-raised)]'
                                  : 'hover:bg-[var(--surface-raised)]'
                              }`}
                            style={isSelected ? { background: 'color-mix(in srgb, var(--aipron-warning) 15%, transparent)' } : undefined}
                          >
                            <td className="py-0.5 pr-2 font-mono">{isSelected ? '✓' : i + 1}</td>
                            <td className="py-0.5 pr-2 font-mono">{d.date}</td>
                            <td className="py-0.5 pr-2 text-right text-[var(--aipron-info)]">{d.arrivals}</td>
                            <td className="py-0.5 pr-2 text-right text-[var(--aipron-accent)]">{d.departures}</td>
                            <td className="py-0.5 text-right font-bold text-[var(--text-primary)]">{d.total}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="mt-2 flex flex-wrap gap-3 text-[10px] pt-2 border-t border-[var(--border)] text-[var(--text-secondary)]">
                  <span><strong>{language === 'es' ? 'Total API calls' : 'Total API calls'}:</strong> ~{top10.estimated_api_calls?.toLocaleString()}</span>
                  <span className="font-bold text-[var(--aipron-warning)]"><strong>{language === 'es' ? 'Coste est.' : 'Est. cost'}:</strong> ~${top10.estimated_cost_usd}</span>
                  <span><strong>{language === 'es' ? 'Rango' : 'Range'}:</strong> {top10.date_range_start} → {top10.date_range_end}</span>
                </div>
              </div>
            );
          })()}

          {/* Track Metadata (if tracks already downloaded) */}
          {project?.analysis_results?.flight_tracks_metadata && !ftDownloading && (
            <div
              className="mt-3 p-3 rounded-lg text-xs border"
              style={{ background: 'color-mix(in srgb, var(--aipron-success) 10%, transparent)', borderColor: 'color-mix(in srgb, var(--aipron-success) 30%, transparent)' }}
            >
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 size={14} className="text-[var(--aipron-success)]" />
                <span className="font-bold text-[var(--aipron-success)]">
                  {language === 'es' ? 'Tracks descargados' : 'Tracks downloaded'}
                </span>
              </div>
              <div className="flex flex-wrap gap-4">
                {project.analysis_results.flight_tracks_metadata.date_range && (
                  <span>{project.analysis_results.flight_tracks_metadata.date_range.start} → {project.analysis_results.flight_tracks_metadata.date_range.end}</span>
                )}
                {project.analysis_results.flight_tracks_metadata.download_stats && (
                  <>
                    <span><strong>{language === 'es' ? 'Vuelos' : 'Flights'}:</strong> {project.analysis_results.flight_tracks_metadata.download_stats.total_flights}</span>
                    <span><strong>{language === 'es' ? 'Puntos' : 'Points'}:</strong> {project.analysis_results.flight_tracks_metadata.download_stats.total_track_points?.toLocaleString()}</span>
                    <span><strong>{language === 'es' ? 'Coste' : 'Cost'}:</strong> ${project.analysis_results.flight_tracks_metadata.download_stats.estimated_cost_usd}</span>
                  </>
                )}
              </div>
            </div>
          )}

        {/* Taxi behaviour headline tiles — moved here from the KPIs module: they
            summarise the same spatial taxi behaviour the map below renders. */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <MetricCard title="Optimal Taxi (Blue Time)"
            value={`${(project?.analysis_results?.advanced_analysis?.blue_time?.optimal_min || 0).toFixed(1)}`}
            sub={language === 'es' ? 'Tiempo óptimo (Bajo Tráfico)' : 'Optimal Time (Low Traffic)'} icon={Clock} isDark={isDark} color="blue" />
          <MetricCard title={language === 'es' ? 'Impacto Congestión' : 'Congestion Impact'}
            value={`${(project?.analysis_results?.advanced_analysis?.blue_time?.impact_min || 0).toFixed(1)}`}
            sub={language === 'es' ? 'Minutos extra por tráfico' : 'Extra minutes due to traffic'} icon={Flame} isDark={isDark} color="orange" />
          <MetricCard title="Complexity Index (CI)" value={(apcas.block2?.tcf || 0).toFixed(3)}
            sub={language === 'es' ? 'Índice de complejidad de taxi' : 'Taxi complexity index'} icon={Activity} isDark={isDark} color="purple" />
        </div>

        <div className="space-y-4">
          {/* Single unified map: infrastructure, analysis heatmaps and AviPLAN
              geometry are all layers of the same Mapbox view (the layer panel
              switches them), instead of two separate map presentations. Full
              width so the layer panel does not cover the airport. */}
          <div>
            {(() => {
              const gl = project?.analysis_results?.advanced_analysis?.geo_layers || {};
              return (
                <UnifiedAirportMap
                  mode="analysis-heatmap"
                  icaoCode={project?.icao_code || 'MPTO'}
                  initialCenter={airportCenter
                    ? [airportCenter.lon, airportCenter.lat] as [number, number]
                    : undefined}
                  initialZoom={15}
                  style={{ height: '600px' }}
                  aviplanData={aviplanData}
                  heatmapData={{
                    taxiRisk:    gl.taxi_risk    ?? null,
                    taxiSpeed:   gl.taxi_speed   ?? null,
                    ciGraph:     gl.complexity_index ?? null,
                    tcfGraph:    gl.tcf_spatial  ?? gl.graph_edges ?? null,
                    mhtGraph:    gl.mean_holding_time ?? null,
                    pushback:    gl.pushback     ?? null,
                    congestion:  gl.tcf_spatial  ?? null,
                    arrivals:    null,
                    departures:  null,
                  }}
                >
                  <AviPLANPanel onUpdate={setAviplanData} />
                </UnifiedAirportMap>
              );
            })()}
          </div>

          {/* Airport Info — below the map now that the map spans full width */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="p-4 rounded-lg bg-[var(--surface-raised)]">
              <p className={`text-xs uppercase font-bold mb-1 ${textSub}`}>
                {language === 'es' ? 'Infraestructura' : 'Infrastructure'}
              </p>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className={`text-sm ${textSub}`}>Stands</span>
                  <span className={`text-sm font-bold ${textMain}`}>{apcas.block4?.n_stands || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm ${textSub}`}>RRC</span>
                  <span className={`text-sm font-bold ${textMain}`}>{apcas.rrc_input || project?.sme_parameters?.runway_capacity || project?.runway_capacity || 30} mov/h</span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm ${textSub}`}>RAC</span>
                  <span className={`text-sm font-bold ${textMain}`}>{safeNum(apcas.rac_capacity, 1)} mov/h</span>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-[var(--surface-raised)]">
              <p className={`text-xs uppercase font-bold mb-1 ${textSub}`}>
                {language === 'es' ? 'Complejidad' : 'Complexity'}
              </p>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className={`text-sm ${textSub}`}>CF Total</span>
                  <span className={`text-sm font-bold ${textMain}`}>{safeNum(apcas.block2?.cf_total, 3) || '1.000'}</span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm ${textSub}`}>TCF</span>
                  <span className={`text-sm font-bold ${textMain}`}>{safeNum((apcas.block2?.tcf || 0) * 100, 1)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm ${textSub}`}>WCF</span>
                  <span className={`text-sm font-bold ${textMain}`}>{safeNum((apcas.block2?.wcf || 0) * 100, 1)}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapPage;
